import { useMemo, useState, type ReactNode } from "react"
import {
  DndContext,
  PointerSensor,
  useDndContext,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core"
import {
  CalendarBlank,
  CaretDown,
  CaretRight,
  Folder,
  FolderPlus,
  Package,
  Plus,
  Storefront,
  Tag,
} from "@phosphor-icons/react"

import { Badge, Button, Input } from "~/components/design-system/components"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { Tooltip } from "~/components/reusable/Tooltip"
import { usePermission } from "~/hooks/usePermission"
import { PACKAGE_NAME_MAX } from "~/utils/allocationPackageOps"
import { pluralize } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import {
  PACKAGE_STATUS_META,
  PACKAGE_TYPE_BADGE,
  PACKAGE_TYPE_LABEL,
  railWindowLabel,
  storesLabel,
} from "./packageDisplay"
import { cannotNestReason, folderNameError, placeAmong, selectionFolderId } from "./packageFolders"
import { PackageIssueBadge } from "./PackageIssueBadge"
import { FolderActionsMenu, PackageActionsMenu } from "./PackageMenus"
import { usePackageNavigator } from "./packageNavigator"
import type { PackageFolder, RangePackage } from "./types"

const ROOT = "root"

type DragItem = { kind: "package" | "folder"; id: string }
type Position = "before" | "after" | "inside"
type Indicator = { key: string; position: Position }

const itemKey = (item: DragItem) => `${item.kind}:${item.id}`
const itemOf = (data: { current?: Record<string, unknown> } | undefined) =>
  data?.current?.item as DragItem | undefined

/** Where on a row the dragged item is: the top or bottom edge places before or after, a folder's middle puts it inside. */
function positionOn(event: DragMoveEvent | DragEndEvent, over: DragItem): Position | null {
  const rect = event.over?.rect
  const dragged = event.active.rect.current.translated
  if (!rect || !dragged) return null
  const ratio = (dragged.top + dragged.height / 2 - rect.top) / rect.height
  if (over.kind === "package") return ratio < 0.5 ? "before" : "after"
  if (ratio < 0.25) return "before"
  if (ratio > 0.75) return "after"
  return "inside"
}

type Tree = {
  folders: PackageFolder[]
  packages: RangePackage[]
  roots: PackageFolder[]
  loose: RangePackage[]
  childrenOf: (folderId: string) => PackageFolder[]
  packagesIn: (folderId: string) => RangePackage[]
  countIn: (folderId: string) => number
}

/**
 * Splits the tree into the active section and the Archived one: archived
 * packages, and archived folders with everything in them, go to Archived. An
 * archived package in an active folder shows on its own there.
 */
function buildTree(packages: RangePackage[], folders: PackageFolder[], archived: boolean): Tree {
  const archivedFolderIds = new Set(
    folders.filter((folder) => folder.deactivated).map((folder) => folder.id),
  )
  const visiblePackages = packages.filter((pkg) => {
    const isArchived =
      pkg.ownDeactivated || (pkg.folderId !== null && archivedFolderIds.has(pkg.folderId))
    return isArchived === archived
  })
  const visibleFolders = folders.filter((folder) => folder.deactivated === archived)
  const visibleIds = new Set(visibleFolders.map((folder) => folder.id))
  const packagesByFolder = Map.groupBy(visiblePackages, (pkg) => pkg.folderId)
  const childrenByParent = Map.groupBy(visibleFolders, (folder) => folder.parentId)
  const childrenOf = (folderId: string) => childrenByParent.get(folderId) ?? []
  const packagesIn = (folderId: string) => packagesByFolder.get(folderId) ?? []
  return {
    folders: visibleFolders,
    packages: visiblePackages,
    roots: visibleFolders.filter(
      (folder) => folder.parentId === null || !visibleIds.has(folder.parentId),
    ),
    loose: visiblePackages.filter((pkg) => pkg.folderId === null || !visibleIds.has(pkg.folderId)),
    childrenOf,
    packagesIn,
    countIn: (folderId) =>
      packagesIn(folderId).length +
      childrenOf(folderId).reduce((sum, child) => sum + packagesIn(child.id).length, 0),
  }
}

/** A row that can be dragged and dropped on, with the reorder line or inside highlight while dragging. */
function DragRow({
  item,
  disabled,
  indicator,
  children,
}: {
  item: DragItem
  disabled: boolean
  indicator: Indicator | null
  children: ReactNode
}) {
  const key = itemKey(item)
  const drag = useDraggable({ id: key, disabled, data: { item } })
  const drop = useDroppable({ id: key, disabled, data: { item } })
  const here = indicator?.key === key ? indicator.position : null
  return (
    <div
      ref={(node) => {
        drag.setNodeRef(node)
        drop.setNodeRef(node)
      }}
      {...drag.attributes}
      {...drag.listeners}
      className={cn(
        "relative rounded-md",
        drag.isDragging && "opacity-50",
        here === "inside" && "bg-subtle-hover",
      )}
    >
      {here === "before" && (
        <div className="pointer-events-none absolute inset-x-1 -top-px h-0.5 rounded-full bg-button-primary" />
      )}
      {children}
      {here === "after" && (
        <div className="pointer-events-none absolute inset-x-1 -bottom-px h-0.5 rounded-full bg-button-primary" />
      )}
    </div>
  )
}

/** The selectable part of a row with its ⋮ menu beside it, shown on hover and on the selected row. */
function RowShell({
  selected,
  onSelect,
  menu,
  children,
}: {
  selected: boolean
  onSelect: () => void
  menu: ReactNode
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-md pr-1 hover:bg-base-hover",
        selected && "bg-base-pressed hover:bg-base-pressed",
      )}
    >
      <Button
        variant="transparent"
        size="sm"
        onClick={onSelect}
        wrapperClassName="min-w-0 flex-1"
        className="h-auto w-full min-w-0 justify-start overflow-hidden rounded-md px-2 py-1.5 hover:bg-transparent [&>div]:w-full [&>div]:min-w-0 [&>div]:justify-start"
      >
        {children}
      </Button>
      <div
        className={cn(
          "shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100",
          selected && "opacity-100",
        )}
      >
        {menu}
      </div>
    </div>
  )
}

function CountBadge({
  icon: Icon,
  count,
  title,
}: {
  icon: typeof Tag
  count: number
  title: string
}) {
  return (
    <Tooltip title={title}>
      <span className="label-xs flex items-center gap-0.5 rounded bg-subtle px-1 text-fg-muted tabular-nums">
        <Icon size={11} />
        {count}
      </span>
    </Tooltip>
  )
}

function PackageHoverBody({ pkg, folderLabel }: { pkg: RangePackage; folderLabel: string }) {
  const status = PACKAGE_STATUS_META[pkg.status]
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col">
        <span className="label-base text-fg-base">{pkg.name}</span>
        <span className="paragraph-sm text-fg-muted">{folderLabel}</span>
      </div>
      <div className="flex gap-1">
        <Badge size="sm" variant={PACKAGE_TYPE_BADGE[pkg.type]}>
          {PACKAGE_TYPE_LABEL[pkg.type]}
        </Badge>
        <Badge size="sm" variant={status.variant}>
          {status.label}
        </Badge>
      </div>
      <dl className="paragraph-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        <dt className="text-fg-muted">Dates</dt>
        <dd>
          {railWindowLabel(pkg)}
          {pkg.datesFolderId !== null && <span className="text-fg-muted"> · from folder</span>}
        </dd>
        <dt className="text-fg-muted">Variants</dt>
        <dd className="tabular-nums">{pkg.productIds.length}</dd>
        <dt className="text-fg-muted">Stores</dt>
        <dd>{storesLabel(pkg)}</dd>
      </dl>
    </div>
  )
}

function PackageRow({
  pkg,
  folderLabel,
  canDrag,
  indicator,
}: {
  pkg: RangePackage
  folderLabel: string
  canDrag: boolean
  indicator: Indicator | null
}) {
  const nav = usePackageNavigator()
  const deleting = nav.draft.isDeleting(pkg.id)
  const dragging = useDndContext().active !== null
  const status = PACKAGE_STATUS_META[pkg.status]
  const target = { kind: "package" as const, id: pkg.id }
  const selected = nav.selection?.kind === "package" && nav.selection.id === pkg.id
  return (
    <DragRow item={target} disabled={!canDrag || deleting} indicator={indicator}>
      <RowShell
        selected={selected}
        onSelect={() => nav.select(target)}
        menu={
          <PackageActionsMenu
            pkg={pkg}
            place="row"
            onRename={() => nav.ask("rename", target)}
            onDelete={() => nav.ask("delete", target)}
          />
        }
      >
        <HoverCard openDelay={500} open={dragging ? false : undefined}>
          <HoverCardTrigger asChild>
            <div className="flex w-full min-w-0 flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <Package size={14} className="shrink-0 text-badge-primary-fg-text" />
                <span
                  className={cn(
                    "label-sm min-w-0 flex-1 truncate text-left text-fg-base",
                    deleting && "text-fg-muted line-through",
                  )}
                >
                  {pkg.name}
                </span>
                <PackageIssueBadge pkg={pkg} />
                <Badge size="xs" variant={PACKAGE_TYPE_BADGE[pkg.type]}>
                  {PACKAGE_TYPE_LABEL[pkg.type]}
                </Badge>
                <Badge size="xs" variant={status.variant}>
                  {status.label}
                </Badge>
              </div>
              <div className="paragraph-xs flex items-center gap-1.5 pl-5 text-fg-muted">
                <CalendarBlank size={11} className="shrink-0" />
                <span className="truncate">
                  {deleting ? "Deleting on save" : railWindowLabel(pkg)}
                </span>
                <span className="flex-1" />
                <CountBadge
                  icon={Tag}
                  count={pkg.productIds.length}
                  title={pluralize(pkg.productIds.length, "variant")}
                />
                <CountBadge
                  icon={Storefront}
                  count={pkg.storeIds.length}
                  title={storesLabel(pkg)}
                />
              </div>
            </div>
          </HoverCardTrigger>
          <HoverCardContent side="right" align="start" sideOffset={12}>
            <PackageHoverBody pkg={pkg} folderLabel={folderLabel} />
          </HoverCardContent>
        </HoverCard>
      </RowShell>
    </DragRow>
  )
}

function FolderRow({
  folder,
  open,
  count,
  canDrag,
  indicator,
  onToggle,
}: {
  folder: PackageFolder
  open: boolean
  count: number
  canDrag: boolean
  indicator: Indicator | null
  onToggle: () => void
}) {
  const nav = usePackageNavigator()
  const deleting = nav.draft.isFolderDeleting(folder.id)
  const target = { kind: "folder" as const, id: folder.id }
  const selected = nav.selection?.kind === "folder" && nav.selection.id === folder.id
  const sub = folder.parentId !== null
  const Caret = open ? CaretDown : CaretRight
  return (
    <DragRow item={target} disabled={!canDrag || deleting} indicator={indicator}>
      <div className="flex items-center">
        <Button
          variant="transparent"
          size="sm"
          aria-label={open ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          onClick={onToggle}
          className="shrink-0 px-1"
        >
          <Caret size={12} />
        </Button>
        <div className="min-w-0 flex-1">
          <RowShell
            selected={selected}
            onSelect={() => nav.select(target)}
            menu={
              <FolderActionsMenu
                folder={folder}
                place="row"
                onRename={() => nav.ask("rename", target)}
                onDelete={() => nav.ask("delete", target)}
              />
            }
          >
            <span
              className={cn(
                "flex shrink-0 items-center justify-center rounded-lg",
                sub ? "size-7 bg-subtle" : "size-9 bg-badge-primary-bg",
              )}
            >
              <Folder size={sub ? 14 : 18} weight="fill" className="text-badge-primary-fg-text" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col text-left">
              <span
                className={cn(
                  "label-sm truncate text-fg-base",
                  deleting && "text-fg-muted line-through",
                )}
              >
                {folder.name}
              </span>
              <span className="paragraph-xs text-fg-muted">
                {deleting ? "Deleting on save" : pluralize(count, "package")}
              </span>
            </span>
          </RowShell>
        </div>
      </div>
    </DragRow>
  )
}

type BranchProps = {
  tree: Tree
  canDrag: boolean
  indicator: Indicator | null
  isOpen: (folderId: string) => boolean
  onToggle: (folderId: string) => void
  /** Shows the "Add package" row under open top-level folders. */
  canAdd: boolean
}

function FolderBranch({ folder, ...props }: BranchProps & { folder: PackageFolder }) {
  const nav = usePackageNavigator()
  const { tree, canDrag, indicator, isOpen, onToggle, canAdd } = props
  const open = isOpen(folder.id)
  const addReason = nav.folderDeletingReason(folder.id)
  const parent =
    folder.parentId === null ? undefined : tree.folders.find((f) => f.id === folder.parentId)
  const folderLabel = parent ? `${parent.name} / ${folder.name}` : folder.name
  return (
    <div>
      <FolderRow
        folder={folder}
        open={open}
        count={tree.countIn(folder.id)}
        canDrag={canDrag}
        indicator={indicator}
        onToggle={() => onToggle(folder.id)}
      />
      {open && (
        <div className="ml-[13px] flex flex-col border-l-2 border-badge-primary-border pl-1">
          {tree.childrenOf(folder.id).map((child) => (
            <FolderBranch key={child.id} folder={child} {...props} />
          ))}
          {tree.packagesIn(folder.id).map((pkg) => (
            <PackageRow
              key={pkg.id}
              pkg={pkg}
              folderLabel={folderLabel}
              canDrag={canDrag}
              indicator={indicator}
            />
          ))}
          {canAdd && folder.parentId === null && (
            <Button
              variant="transparent"
              size="sm"
              requires="edit"
              allowedTitle={addReason ?? `Add a package to ${folder.name}`}
              disabled={addReason !== undefined}
              wrapperClassName="w-full"
              className="w-full justify-start gap-1.5 text-fg-muted"
              onClick={() => nav.newPackage(folder.id)}
            >
              <Plus size={12} />
              Add package
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/** The name-only step of New folder: Enter creates an empty active folder, Esc cancels. */
function NewFolderName({
  siblings,
  onCreate,
  onCancel,
}: {
  siblings: PackageFolder[]
  onCreate: (name: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState("")
  const taken = new Set(siblings.map((folder) => folder.name.trim().toLowerCase()))
  const error = value.trim() === "" ? undefined : folderNameError(value, taken)
  return (
    <div className="flex flex-col gap-1">
      <Input
        autoFocus
        aria-label="New folder name"
        placeholder="Folder name, e.g. AW26"
        value={value}
        maxLength={PACKAGE_NAME_MAX}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && value.trim() !== "" && !error) onCreate(value.trim())
          if (event.key === "Escape") onCancel()
        }}
        onBlur={() => {
          if (value.trim() === "") onCancel()
        }}
      />
      <p className={cn("paragraph-xs", error ? "text-fg-negative-text" : "text-fg-muted")}>
        {error ?? "Press Enter to create the folder, Esc to cancel."}
      </p>
    </div>
  )
}

function NewMenu({
  newPackageReason,
  onNewFolder,
  onNewPackage,
}: {
  /** Why New package can't use the open folder. */
  newPackageReason: string | undefined
  onNewFolder: () => void
  onNewPackage: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger requires="edit" allowedTitle="New folder or package" asChild>
        <Button variant="neutral" size="sm" className="gap-1">
          <Plus size={14} />
          New
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuItem onSelect={onNewFolder} className="items-start">
          <FolderPlus size={16} className="mt-0.5" />
          <span className="flex flex-col">
            New folder
            <span className="paragraph-sm text-fg-muted">
              Holds packages with shared dates and stores
            </span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onNewPackage}
          disabled={newPackageReason !== undefined}
          className="items-start"
        >
          <Package size={16} className="mt-0.5" />
          <span className="flex flex-col">
            New package
            <span className="paragraph-sm text-fg-muted">
              {newPackageReason ?? "Products for a folder, uses its settings"}
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The bottom target while dragging something that can leave its folder. */
function TopLevelTarget({ label }: { label: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: ROOT })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "label-sm m-2 rounded-md border border-dashed border-badge-primary-border p-3 text-center text-badge-primary-fg-text",
        isOver && "bg-badge-primary-bg",
      )}
    >
      {label}
    </div>
  )
}

/** The selection's folder and its parent, which open on their own. */
function openOnSelection(folderId: string | null, folders: PackageFolder[]) {
  const folder = folders.find((other) => other.id === folderId)
  if (!folder) return new Set<string>()
  return new Set(folder.parentId === null ? [folder.id] : [folder.id, folder.parentId])
}

export function PackageFolderTree() {
  const nav = usePackageNavigator()
  const { packages, folders, selection } = nav
  const { allowed } = usePermission("edit")
  const [opened, setOpened] = useState<Set<string>>(new Set())
  const [closed, setClosed] = useState<Set<string>>(new Set())
  const [archivedOpen, setArchivedOpen] = useState(false)
  const [naming, setNaming] = useState(false)
  const [dragging, setDragging] = useState<DragItem | null>(null)
  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const active = useMemo(() => buildTree(packages, folders, false), [packages, folders])
  const archived = useMemo(() => buildTree(packages, folders, true), [packages, folders])
  const archivedCount = archived.roots.length + archived.loose.length
  const contextFolderId = selectionFolderId(selection, packages)
  const current = openOnSelection(contextFolderId, folders)
  const isOpen = (folderId: string) =>
    !closed.has(folderId) && (opened.has(folderId) || current.has(folderId))
  const toggle = (folderId: string) => {
    const open = isOpen(folderId)
    const without = (prev: Set<string>) => new Set([...prev].filter((id) => id !== folderId))
    const plus = (prev: Set<string>) => new Set([...prev, folderId])
    setOpened(open ? without : plus)
    setClosed(open ? plus : without)
  }

  const packageById = new Map(packages.map((pkg) => [pkg.id, pkg]))
  const folderById = new Map(folders.map((folder) => [folder.id, folder]))

  /** Sibling ids in display order: the active ones, then the archived ones, so they keep their place after. */
  const siblingIds = <T extends { id: string }>(activeList: T[], archivedList: T[]) => [
    ...activeList.map((item) => item.id),
    ...archivedList.map((item) => item.id),
  ]

  /** A folder marked for deletion takes nothing new: whatever lands there is deleted on Save. */
  const deleting = (folderId: string | null) => nav.folderDeletingReason(folderId) !== undefined

  /** What dropping `item` on `over` (null: the top-level target) at `position` does, or null when it can't land there. */
  const resolveDrop = (item: DragItem, over: DragItem | null, position: Position | null) => {
    if (over === null) {
      if (item.kind === "package") return { kind: "movePackage" as const, folderId: null }
      return { kind: "moveFolder" as const, parentId: null }
    }
    if (!position || (over.kind === item.kind && over.id === item.id)) return null
    // Something marked for deletion stays put until the deletion is saved or discarded.
    if (item.kind === "package" ? nav.draft.isDeleting(item.id) : deleting(item.id)) return null
    if (item.kind === "package") {
      const pkg = packageById.get(item.id)
      if (!pkg) return null
      if (over.kind === "folder") {
        if (over.id === pkg.folderId || deleting(over.id)) return null
        return { kind: "movePackage" as const, folderId: over.id }
      }
      const target = packageById.get(over.id)
      if (!target || position === "inside") return null
      if (target.folderId !== pkg.folderId) {
        // Moving to the top level goes through the bottom target.
        if (target.folderId === null || deleting(target.folderId)) return null
        return { kind: "movePackage" as const, folderId: target.folderId }
      }
      const inSameFolder = (other: RangePackage) => other.folderId === pkg.folderId
      return {
        kind: "reorder" as const,
        reorder: "package" as const,
        ids: placeAmong(
          siblingIds(active.packages.filter(inSameFolder), archived.packages.filter(inSameFolder)),
          pkg.id,
          target.id,
          position,
        ),
      }
    }
    const folder = folderById.get(item.id)
    const target = over.kind === "folder" ? folderById.get(over.id) : undefined
    if (!folder || !target) return null
    if (position === "inside") {
      return cannotNestReason(folders, folder.id, target.id) === undefined && !deleting(target.id)
        ? { kind: "moveFolder" as const, parentId: target.id }
        : null
    }
    if (target.parentId === folder.parentId) {
      const sameParent = (other: PackageFolder) => other.parentId === folder.parentId
      return {
        kind: "reorder" as const,
        reorder: "folder" as const,
        ids: placeAmong(
          siblingIds(active.folders.filter(sameParent), archived.folders.filter(sameParent)),
          folder.id,
          target.id,
          position,
        ),
      }
    }
    return null
  }

  /** The dragged item, and what it is over: an item, the top-level target (null), or nothing. */
  const dropOf = (event: DragMoveEvent | DragEndEvent) => {
    const item = itemOf(event.active.data)
    if (!item || !event.over) return null
    if (event.over.id === ROOT) return { item, over: null, position: null }
    const over = itemOf(event.over.data)
    if (!over) return null
    return { item, over, position: positionOn(event, over) }
  }

  const onDragStart = ({ active: dragged }: DragStartEvent) =>
    setDragging(itemOf(dragged.data) ?? null)

  const onDragMove = (event: DragMoveEvent) => {
    const drop = dropOf(event)
    if (!drop?.over || !drop.position) return setIndicator(null)
    const result = resolveDrop(drop.item, drop.over, drop.position)
    if (!result) return setIndicator(null)
    if (result.kind === "reorder") {
      return setIndicator({ key: itemKey(drop.over), position: drop.position })
    }
    const folderId = result.kind === "movePackage" ? result.folderId : result.parentId
    setIndicator(
      folderId === null
        ? null
        : { key: itemKey({ kind: "folder", id: folderId }), position: "inside" },
    )
  }

  const endDrag = () => {
    setDragging(null)
    setIndicator(null)
  }

  const onDragEnd = (event: DragEndEvent) => {
    endDrag()
    const drop = dropOf(event)
    if (!drop) return
    const result = resolveDrop(drop.item, drop.over, drop.position)
    if (!result) return
    if (result.kind === "reorder") return nav.reorder(result.reorder, result.ids)
    if (result.kind === "movePackage") return nav.movePackage(drop.item.id, result.folderId)
    nav.moveFolder(drop.item.id, result.parentId)
  }

  const topLevelLabel = (() => {
    if (!dragging) return null
    if (dragging.kind === "package") {
      return packageById.get(dragging.id)?.folderId == null ? null : "Move to the top level"
    }
    return folderById.get(dragging.id)?.parentId == null ? null : "Make it a top-level folder"
  })()

  const branchProps: BranchProps = {
    tree: active,
    canDrag: allowed,
    indicator,
    isOpen,
    onToggle: toggle,
    canAdd: allowed,
  }

  return (
    <div className="flex w-[320px] shrink-0 flex-col border-r border-border-base">
      <div className="flex items-center gap-2 border-b border-border-base px-3 py-2">
        <span className="label-base flex-1 text-fg-base">Packages</span>
        <NewMenu
          newPackageReason={nav.folderDeletingReason(contextFolderId)}
          onNewFolder={() => setNaming(true)}
          onNewPackage={() => nav.newPackage(contextFolderId)}
        />
      </div>
      {naming && (
        <div className="border-b border-border-base p-3">
          <NewFolderName
            siblings={folders.filter((folder) => folder.parentId === null)}
            onCreate={(name) => {
              setNaming(false)
              nav.createFolder(name, null)
            }}
            onCancel={() => setNaming(false)}
          />
        </div>
      )}

      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={endDrag}
      >
        <div className="flex flex-1 flex-col gap-0.5 overflow-x-hidden overflow-y-auto p-1">
          {active.roots.map((folder) => (
            <FolderBranch key={folder.id} folder={folder} {...branchProps} />
          ))}
          {active.loose.map((pkg) => (
            <PackageRow
              key={pkg.id}
              pkg={pkg}
              folderLabel="No folder"
              canDrag={allowed}
              indicator={indicator}
            />
          ))}
          {active.roots.length === 0 && active.loose.length === 0 && (
            <p className="p-4 text-center text-sm text-fg-subtle">
              {folders.length + packages.length === 0
                ? "No folders yet. A folder holds the packages that share dates and stores, like AW26."
                : "Nothing here yet"}
            </p>
          )}
          {archivedCount > 0 && (
            <div className="mt-2 border-t border-border-base pt-1">
              <Button
                variant="transparent"
                size="sm"
                className="w-full justify-start gap-1"
                wrapperClassName="w-full"
                onClick={() => setArchivedOpen(!archivedOpen)}
              >
                {archivedOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                Archived
                <span className="paragraph-xs text-fg-muted tabular-nums">{archivedCount}</span>
              </Button>
              {archivedOpen && (
                <>
                  {archived.roots.map((folder) => (
                    <FolderBranch
                      key={folder.id}
                      folder={folder}
                      {...branchProps}
                      tree={archived}
                      canDrag={false}
                      canAdd={false}
                    />
                  ))}
                  {archived.loose.map((pkg) => (
                    <PackageRow
                      key={pkg.id}
                      pkg={pkg}
                      folderLabel={
                        pkg.folderId === null
                          ? "No folder"
                          : `${folderById.get(pkg.folderId)?.name ?? "Folder"} · archived`
                      }
                      canDrag={false}
                      indicator={null}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        {topLevelLabel && <TopLevelTarget label={topLevelLabel} />}
      </DndContext>
    </div>
  )
}
